/**
 * SodWarningBanner — banner inline com conflitos SoD detectados.
 *
 * Aparece como tone="danger" (bloqueio) ou tone="warning" (aviso) acima
 * da PermissionMatrix quando o set selecionado gera conflito. Os pares
 * conflitantes sao listados com seus motivos e severidades.
 *
 * Quando ha conflito de severidade `aviso`, exibe input de justificativa
 * e botao "Aceitar mesmo assim" — opt-in explicito documentado em audit.
 *
 * Props:
 * - `conflitos`: [{ a, b, motivo, severidade }]
 * - `onAcceptAnyway(justificativa)`: callback ao aceitar (somente avisos)
 * - `onDismiss()`: callback opcional para esconder o banner sem agir
 * - `nomePermissao(codigo)`: opcional — nome legivel da permissao (o mesmo
 *   da matriz). Sem ele, mostra o codigo tecnico.
 */
import React, { useState } from "react";
import styled from "styled-components";
import { FiAlertTriangle, FiInfo } from "react-icons/fi";

import { getToneTokens } from "../../styles/shared";

const Banner = styled.div`
  border-radius: 12px;
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  border: 1px solid
    ${({ theme, $tone }) => getToneTokens(theme, $tone).border};
  background: ${({ theme, $tone }) => getToneTokens(theme, $tone).background};
  color: ${({ theme, $tone }) => getToneTokens(theme, $tone).text};
`;

const Heading = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  font-size: 0.9rem;
`;

const List = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Item = styled.li`
  font-size: 0.85rem;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const JustificativaInput = styled.textarea`
  width: 100%;
  min-height: 60px;
  padding: 8px 10px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.surface};
  color: ${({ theme }) => theme.textPrimary};
  font-size: 0.85rem;
  resize: vertical;
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
`;

const BotaoAceitar = styled.button`
  background: ${({ theme }) => theme.warningText};
  color: #fff;
  border: 0;
  border-radius: 8px;
  padding: 6px 12px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
`;

const BotaoDismiss = styled.button`
  background: transparent;
  color: inherit;
  border: 1px solid currentColor;
  border-radius: 8px;
  padding: 6px 12px;
  font-size: 0.85rem;
  cursor: pointer;
  opacity: 0.7;

  &:hover {
    opacity: 1;
  }
`;

const semTraducao = (codigo) => codigo;

function SodWarningBanner({ conflitos = [], onAcceptAnyway, onDismiss, nomePermissao = semTraducao }) {
  const [justificativa, setJustificativa] = useState("");
  if (!conflitos.length) return null;

  const temBloqueio = conflitos.some((c) => c.severidade === "bloqueio");
  const tone = temBloqueio ? "danger" : "warning";
  const podeAceitar = !temBloqueio && typeof onAcceptAnyway === "function";

  return (
    <Banner role="alert" $tone={tone}>
      <Heading>
        {temBloqueio ? (
          <FiAlertTriangle size={18} aria-hidden="true" />
        ) : (
          <FiInfo size={18} aria-hidden="true" />
        )}
        {temBloqueio
          ? `${conflitos.length} conflito(s) de SoD bloqueiam esta atribuição`
          : `${conflitos.length} aviso(s) de SoD detectado(s)`}
      </Heading>

      <List>
        {conflitos.map((c, idx) => (
          <Item key={`${c.a}-${c.b}-${idx}`}>
            <span>
              <strong title={c.a}>{nomePermissao(c.a)}</strong>{" "}
              <span aria-label="conflita com">⚡</span>{" "}
              <strong title={c.b}>{nomePermissao(c.b)}</strong>
            </span>
            {c.motivo && <small>{c.motivo}</small>}
          </Item>
        ))}
      </List>

      {podeAceitar && (
        <>
          <JustificativaInput
            placeholder="Justificativa (obrigatória para aceitar avisos de SoD)"
            value={justificativa}
            onChange={(e) => setJustificativa(e.target.value)}
            aria-label="Justificativa para aceitar conflito"
          />
          <Actions>
            {onDismiss && (
              <BotaoDismiss type="button" onClick={onDismiss}>
                Cancelar
              </BotaoDismiss>
            )}
            <BotaoAceitar
              type="button"
              disabled={!justificativa.trim()}
              onClick={() => onAcceptAnyway?.(justificativa.trim())}
            >
              Aceitar mesmo assim
            </BotaoAceitar>
          </Actions>
        </>
      )}
    </Banner>
  );
}

export default SodWarningBanner;
